import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { ChevronLeft, ChevronRight, Award, Info, Loader2, Lock, Undo2 } from 'lucide-react'
import type { PageMeta } from '../../../lib/permissions/pageMeta'
import { PageHeader } from '../../../components/ui/PageHeader'
import { EmptyState } from '../../../components/ui/EmptyState'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { useCurrency } from '../../../hooks/useCurrency'
import { usePermissions } from '../../../hooks/usePermissions'
import { useAuthStore } from '../../../store/authStore'
import { displayDateTime } from '../../../utils/dates'
import { fetchSalesPerRep } from '../../../lib/salesRepReport'
import {
  fetchIncentivePeriod,
  fetchIncentivePeriodReps,
  saveIncentiveDraft,
  postIncentiveAccrual,
  voidIncentiveAccrual,
  startNewIncentiveDraft,
  type IncentivePeriod,
  type IncentivePeriodRep,
} from '../../../lib/incentives'

export const pageMeta: PageMeta = {
  key: 'trading.incentives',
  label: 'Incentives',
  module: 'Trading',
  section: 'Incentives',
  description: 'Post monthly sales-rep incentive accruals',
  defaultPermissions: {
    owner: 'edit', admin: 'edit', manager: 'edit',
    accountant: 'none', cashier: 'none', viewer: 'none',
  },
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const pad = (n: number) => String(n).padStart(2, '0')
const round2 = (n: number) => Math.round(n * 100) / 100
const MAX_RATE = 100

// Parse a rate input → clamped number in [0, MAX_RATE], or null when blank.
// Empty → null (use base for overrides; 0 for the base rate). Non-numeric → null.
// Negative → 0. Above MAX_RATE → MAX_RATE. This is the FIRST line of defense;
// the DB CHECK (>= 0) is the last.
function parseRate(raw: string): number | null {
  if (raw == null || raw.trim() === '') return null
  const n = Number(raw)
  if (Number.isNaN(n)) return null
  return Math.min(MAX_RATE, Math.max(0, n))
}
const normalizeRate = (v: string): string => {
  const p = parseRate(v)
  return p == null ? '' : String(p)
}

function defaultPeriod(): { year: number; month: number } {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() - 1) // most recent completed month
  return { year: d.getFullYear(), month: d.getMonth() + 1 }
}

// A merged per-rep row for display: live (draft) or snapshot (posted/voided).
interface ViewRow {
  repId: string
  repName: string
  gross: number
  salesCount: number
  overrideStr: string        // '' = use base rate
  applicableRate: number
  amount: number
  kind?: 'staff' | 'external'   // known on posted/voided snapshots; external = track-only
}

export default function IncentivesPage() {
  const { tenant, user } = useAuthStore()
  const tid = tenant?.id ?? ''
  const userId = user?.id ?? null
  const { format } = useCurrency()
  const { can } = usePermissions()
  const canPost = can('incentives.post_accrual')
  const canVoid = can('incentives.void_accrual')
  const qc = useQueryClient()

  const init = defaultPeriod()
  const [year, setYear] = useState(init.year)
  const [month, setMonth] = useState(init.month)

  const [baseRateInput, setBaseRateInput] = useState('')
  const [overrides, setOverrides] = useState<Map<string, string>>(new Map())
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [voidOpen, setVoidOpen] = useState(false)
  const [voidReason, setVoidReason] = useState('')

  const now = new Date()
  const curYear = now.getFullYear()
  const curMonth = now.getMonth() + 1
  const isPast = year < curYear || (year === curYear && month < curMonth)
  const atCurrentOrFuture = year > curYear || (year === curYear && month >= curMonth)

  const monthStart = `${year}-${pad(month)}-01`
  const monthEnd = `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`
  const monthLabel = `${MONTHS[month - 1]} ${year}`

  // ── data ────────────────────────────────────────────────────────────────
  const periodQuery = useQuery<IncentivePeriod | null>({
    queryKey: ['incentive-period', tid, year, month],
    enabled: !!tid,
    queryFn: () => fetchIncentivePeriod(tid, year, month),
    refetchOnWindowFocus: false,
  })
  const period = periodQuery.data ?? null
  const status = period?.status ?? 'draft'
  const isDraft = status === 'draft'
  const isPosted = status === 'posted'
  const isVoided = status === 'voided'
  const editable = isDraft && canPost

  const repsQuery = useQuery<IncentivePeriodRep[]>({
    queryKey: ['incentive-period-reps', tid, period?.id ?? null],
    enabled: !!tid && !!period?.id,
    queryFn: () => fetchIncentivePeriodReps(tid, period!.id),
    refetchOnWindowFocus: false,
  })
  const reps = useMemo(() => repsQuery.data ?? [], [repsQuery.data])

  const salesQuery = useQuery({
    queryKey: ['incentive-sales', tid, monthStart, monthEnd],
    enabled: !!tid,
    queryFn: () => fetchSalesPerRep(tid, { from: monthStart, to: monthEnd, search: '' }),
    refetchOnWindowFocus: false,
  })
  const salesRows = useMemo(
    () => (salesQuery.data?.rows ?? []).filter(r => r.repId !== null),
    [salesQuery.data],
  )

  // ── sync editable state when the selected month / loaded data changes ─────
  useEffect(() => {
    setBaseRateInput(period ? String(period.baseRatePercent) : '')
  }, [period?.id, period?.baseRatePercent, year, month]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const m = new Map<string, string>()
    for (const r of reps) {
      if (r.representativeId && r.overrideRatePercent != null) {
        m.set(r.representativeId, String(r.overrideRatePercent))
      }
    }
    setOverrides(m)
  }, [reps, year, month])

  // ── merged view rows + total ──────────────────────────────────────────────
  const base = parseRate(baseRateInput) ?? 0

  const { rows, total, paidCount } = useMemo(() => {
    let rows: ViewRow[] = []
    if (isPosted || isVoided) {
      // Frozen snapshot — show exactly what was posted.
      rows = reps
        .filter(r => r.representativeId)
        .map(r => ({
          repId: r.representativeId as string,
          repName: r.representativeName ?? 'Unknown rep',
          gross: r.snapshotSalesGross ?? 0,
          salesCount: r.snapshotSalesCount ?? 0,
          overrideStr: r.overrideRatePercent == null ? '' : String(r.overrideRatePercent),
          applicableRate: r.overrideRatePercent ?? period?.baseRatePercent ?? 0,
          amount: r.calculatedIncentiveAmount ?? 0,
          kind: r.representativeKind,
        }))
        .sort((a, b) => b.amount - a.amount)
    } else {
      // Draft — live compute from period sales + current overrides.
      rows = salesRows.map(r => {
        const ovStr = overrides.get(r.repId as string) ?? ''
        const ovNum = parseRate(ovStr)
        const applicable = ovNum == null ? base : ovNum
        return {
          repId: r.repId as string,
          repName: r.repName,
          gross: r.gross,
          salesCount: r.salesCount,
          overrideStr: ovStr,
          applicableRate: applicable,
          amount: round2(r.gross * applicable / 100),
        }
      })
    }
    const total = round2(rows.reduce((s, r) => s + r.amount, 0))
    const paidCount = rows.filter(r => r.amount > 0).length
    return { rows, total, paidCount }
  }, [isPosted, isVoided, reps, salesRows, overrides, base, period?.baseRatePercent])

  // ── mutations ─────────────────────────────────────────────────────────────
  function buildDraftInput() {
    return {
      tenantId: tid,
      year,
      month,
      baseRatePercent: parseRate(baseRateInput) ?? 0,
      overrides: salesRows.map(r => {
        const ovStr = overrides.get(r.repId as string) ?? ''
        return {
          representativeId: r.repId as string,
          representativeName: r.repName,
          overrideRatePercent: parseRate(ovStr),
        }
      }),
    }
  }

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['incentive-period', tid, year, month] })
    qc.invalidateQueries({ queryKey: ['incentive-period-reps', tid] })
  }

  const saveMut = useMutation({
    mutationFn: () => saveIncentiveDraft(buildDraftInput()),
    onSuccess: () => { toast.success(`Draft saved for ${monthLabel}.`); invalidate() },
    onError: (e: Error) => toast.error(e.message),
  })

  const postMut = useMutation({
    mutationFn: async () => {
      // Persist the on-screen rates FIRST so the RPC recalculates against them.
      const p = await saveIncentiveDraft(buildDraftInput())
      return postIncentiveAccrual({ tenantId: tid, periodId: p.id, postedBy: userId })
    },
    onSuccess: (res) => {
      setConfirmOpen(false)
      toast.success(
        res.posted
          ? `Posted ${format(res.total)} across ${res.repCount} rep(s) for ${monthLabel}.`
          : `Recorded ${monthLabel} with a zero accrual (no journal).`,
      )
      invalidate()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const voidMut = useMutation({
    mutationFn: () => voidIncentiveAccrual({
      tenantId: tid,
      periodId: period!.id,
      voidReason: voidReason.trim(),
      voidedBy: userId,
    }),
    onSuccess: (res) => {
      setVoidOpen(false)
      setVoidReason('')
      toast.success(
        res.voidJournalId
          ? `Voided ${monthLabel} — reversed ${format(res.totalReversed)} across ${res.repCountReversed} rep(s).`
          : `Voided ${monthLabel}.`,
      )
      invalidate()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const newDraftMut = useMutation({
    mutationFn: () => startNewIncentiveDraft({ tenantId: tid, year, month }),
    onSuccess: () => { toast.success(`New draft started for ${monthLabel}.`); invalidate() },
    onError: (e: Error) => toast.error(e.message),
  })

  const busy = saveMut.isPending || postMut.isPending
  const loading = periodQuery.isLoading || salesQuery.isLoading || (!!period?.id && repsQuery.isLoading)

  function step(delta: number) {
    const d = new Date(year, month - 1 + delta, 1)
    setYear(d.getFullYear())
    setMonth(d.getMonth() + 1)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Incentives"
        subtitle="Post monthly sales-rep incentive accruals"
      />

      {/* Month selector + status */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => step(-1)} className="touch-target rounded-xl p-2 text-gray-500 hover:bg-gray-100" aria-label="Previous month">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="min-w-[9.5rem] text-center text-base font-semibold text-gray-900">{monthLabel}</span>
          <button type="button" onClick={() => step(1)} disabled={atCurrentOrFuture} className="touch-target rounded-xl p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-30" aria-label="Next month">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <StatusBadge period={period} />
      </div>

      {/* Voided banner + start-new-draft */}
      {isVoided && (
        <div className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <p className="font-semibold">This period was voided.</p>
              {period?.voidReason && <p className="mt-0.5">Reason: {period.voidReason}</p>}
              {period?.voidedAt && <p className="text-xs text-red-500">Voided {displayDateTime(period.voidedAt)}</p>}
            </div>
          </div>
          {canPost && (
            <button
              type="button"
              onClick={() => newDraftMut.mutate()}
              disabled={newDraftMut.isPending}
              className="btn-secondary flex-shrink-0 disabled:opacity-50"
            >
              {newDraftMut.isPending ? 'Starting…' : `Start new draft for ${MONTHS[month - 1]}`}
            </button>
          )}
        </div>
      )}

      {/* Base rate + summary */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Base rate</span>
          {editable ? (
            <span className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2 focus-within:ring-2 focus-within:ring-brand-500">
              <input
                type="number" min={0} max={MAX_RATE} step="0.01" inputMode="decimal"
                value={baseRateInput}
                onChange={e => setBaseRateInput(e.target.value)}
                onBlur={() => setBaseRateInput(normalizeRate)}
                className="w-20 border-0 bg-transparent py-1.5 text-right text-sm focus:outline-none focus:ring-0"
                placeholder="0"
              />
              <span className="pl-1 text-gray-400">%</span>
            </span>
          ) : (
            <strong className="text-gray-900">{(period?.baseRatePercent ?? 0).toFixed(2)}%</strong>
          )}
        </label>
        <span className="text-sm text-gray-500">Reps: <strong className="text-gray-900">{rows.length}</strong></span>
        <span className="text-sm text-gray-500">Total incentive: <strong className="text-gray-900">{format(total)}</strong></span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Award className="h-8 w-8" />}
          title="No rep sales in this period"
          description={
            isDraft
              ? 'No representative-attributed sales were found. You can still post — it records a zero accrual with no journal.'
              : 'This period was posted with no rep sales.'
          }
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <th className="px-4 py-3">Representative</th>
                  <th className="px-4 py-3 text-right">Sales</th>
                  <th className="px-4 py-3 text-right">Gross</th>
                  <th className="px-4 py-3 text-right">Override %</th>
                  <th className="px-4 py-3 text-right">Rate</th>
                  <th className="px-4 py-3 text-right">Incentive</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.repId} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      <span className="inline-flex items-center gap-1.5">
                        {r.repName}
                        {r.kind === 'external' && (
                          <span
                            className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500"
                            title="Non-staff representative — tracked only, not posted to the GL or settled in-app"
                          >
                            non-staff
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{r.salesCount}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">{format(r.gross)}</td>
                    <td className="px-4 py-3 text-right">
                      {editable ? (
                        <span className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-1.5 focus-within:ring-2 focus-within:ring-brand-500">
                          <input
                            type="number" min={0} max={MAX_RATE} step="0.01" inputMode="decimal"
                            value={r.overrideStr}
                            onChange={e => setOverrides(prev => new Map(prev).set(r.repId, e.target.value))}
                            onBlur={() => setOverrides(prev => { const n = new Map(prev); n.set(r.repId, normalizeRate(n.get(r.repId) ?? '')); return n })}
                            className="w-16 border-0 bg-transparent py-1 text-right text-sm focus:outline-none focus:ring-0"
                            placeholder="base"
                          />
                        </span>
                      ) : (
                        r.overrideStr === '' ? <span className="text-gray-300">—</span> : `${Number(r.overrideStr).toFixed(2)}%`
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">{r.applicableRate.toFixed(2)}%</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{format(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-100 bg-gray-50/60 font-semibold text-gray-900">
                  <td className="px-4 py-3" colSpan={5}>Total</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{format(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Actions (draft + permitted only) */}
      {editable && (
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
          {!isPast && (
            <span className="flex items-center gap-1.5 text-xs text-gray-400 sm:mr-auto">
              <Lock className="h-3.5 w-3.5" /> Posting opens after {monthLabel} ends.
            </span>
          )}
          <button type="button" onClick={() => saveMut.mutate()} disabled={busy} className="btn-secondary disabled:opacity-50">
            {saveMut.isPending ? 'Saving…' : 'Save Draft'}
          </button>
          <button type="button" onClick={() => setConfirmOpen(true)} disabled={busy || !isPast} className="btn-primary disabled:opacity-50">
            Post Incentive for {MONTHS[month - 1]}
          </button>
        </div>
      )}

      {isPosted && (
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-400">
            {period?.postedJournalId
              ? 'Rates are locked. Void to reverse the accrual and start a new draft.'
              : 'Recorded with a zero accrual — no journal was written.'}
          </p>
          {canVoid && (
            <button
              type="button"
              onClick={() => { setVoidReason(''); setVoidOpen(true) }}
              className="btn-danger flex items-center justify-center gap-1.5 sm:w-auto"
            >
              <Undo2 className="h-4 w-4" /> Void
            </button>
          )}
        </div>
      )}

      {/* Post confirmation */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title={`Post incentive for ${monthLabel}?`} size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This posts a <strong>{format(total)}</strong> accrual across <strong>{paidCount}</strong> representative(s)
            {total <= 0 && ' (zero total — the period is recorded but no journal is written)'}. Rates lock once posted.
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setConfirmOpen(false)} disabled={postMut.isPending} className="btn-secondary">Cancel</button>
            <button type="button" onClick={() => postMut.mutate()} disabled={postMut.isPending} className="btn-primary disabled:opacity-50">
              {postMut.isPending ? <span className="inline-flex items-center gap-1.5"><Loader2 className="h-4 w-4 animate-spin" /> Posting…</span> : 'Post'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Void confirmation */}
      <Modal open={voidOpen} onClose={() => setVoidOpen(false)} title={`Void incentive for ${monthLabel}?`} size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This reverses the posted accrual{period?.postedJournalId ? '' : ' record'} and marks the period voided.
            You'll need to <strong>Start new draft</strong> to re-post for this month.
          </p>
          <div>
            <label className="label">Reason <span className="text-red-500">*</span></label>
            <textarea
              value={voidReason}
              onChange={e => setVoidReason(e.target.value)}
              rows={3}
              className="input"
              placeholder="Why is this accrual being voided?"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setVoidOpen(false)} disabled={voidMut.isPending} className="btn-secondary">Cancel</button>
            <button
              type="button"
              onClick={() => voidMut.mutate()}
              disabled={voidMut.isPending || voidReason.trim() === ''}
              className="btn-danger disabled:opacity-50"
            >
              {voidMut.isPending ? <span className="inline-flex items-center gap-1.5"><Loader2 className="h-4 w-4 animate-spin" /> Voiding…</span> : 'Void accrual'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function StatusBadge({ period }: { period: IncentivePeriod | null }) {
  const status = period?.status ?? 'draft'
  if (status === 'posted') {
    return (
      <span className="badge badge-green">
        Posted{period?.postedAt ? ` · ${displayDateTime(period.postedAt)}` : ''}
      </span>
    )
  }
  if (status === 'voided') {
    return <span className="badge badge-red">Voided</span>
  }
  return <span className="badge badge-gray">Draft</span>
}
